import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';

import { noTextSelect } from '../../common/CssCommon';
import Dialog from '../Dialog';
import { closeDialog } from '../../../store/modules/dialog';
import IconCanvas from '../../../img/activeLink/ribbonIcon/ribbon_big_ico_canvers.png';
import IconCanvas2x from '../../../img/activeLink/ribbonIcon/ribbon_big_ico_canvers@2x.png';
import { usePOWebofficeSDKState } from '../../../context/poWebOfficeSDKContext';
import {
  genPWCNewDocument,
  getPWCCurrentLoginStatus,
  getPWCDocInfo,
  getReauthorizationUrl
} from '../../plugin/pwc/pwcApis';
import { insertActiveLink } from '../../plugin/activeLink';
import ga from '../../../utils/gaConnect';
import {
  initPWCConnectedData,
  setPWCConnectedData,
  usePwcAuthDataSliceSelector
} from '../../../store/modules/pwcAuthData';
import Button from '../../common/Button';
import CONSTANT from '../../../define/constant';
import Radio from '../../common/Radio';
import TextInput from '../../common/TextInput';

const DialogBody = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-end;
  box-sizing: border-box;
  margin: 16px 20px 10px 20px;
  ${noTextSelect}
`;

const DialogMessage = styled.div`
  width: 400px;
  display: flex;
  font-size: 14px;
  color: #2f3133;
`;

const FooterWrapper = styled.div`
  width: 100%;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 18px 20px;
  box-sizing: border-box;
`;

const ButtonWapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: flex-end;
  align-items: center;
`;

const DialogBodyInsertCanvas = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 11px;
`;

const Warning = styled.div`
  height: 15px;
  font-size: 13px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #f33333;
`;

const InputArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const INSERT_TYPE = {
  NEW_CANVAS: 'NEW_CANVAS',
  EXIST_CANVAS: 'EXIST_CANVAS'
};

const PWCServerURL = process.env.REACT_APP_PWC_API_SERVER;

export default function ActiveLinkCanvas({ isShow }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const poSDKContextState = usePOWebofficeSDKState();

  const pwcAuthData = usePwcAuthDataSliceSelector();

  const [insertInfo, setInsertInfo] = useState({ type: INSERT_TYPE.NEW_CANVAS, url: '' });
  const [warning, setWarning] = useState('');

  useEffect(() => {
    getPWCCurrentLoginStatus().catch(() => dispatch(initPWCConnectedData()));
  }, []);

  const onDialogClose = useCallback(() => dispatch(closeDialog()), [dispatch]);

  const insert = useCallback(
    (url) => {
      try {
        if (url) {
          insertActiveLink(poSDKContextState, url);
        } else {
          genPWCNewDocument().then((pwcURL) => insertActiveLink(poSDKContextState, pwcURL));
        }
      } catch (err) {
        ga.event({ category: '액티브링크', action: '추가실패', label: '공동작업(캔버스)' });
        throw err;
      }
    },
    [poSDKContextState]
  );

  const reqLogin = useCallback(() => {
    return new Promise((resolve, reject) => {
      let timer = undefined;
      try {
        const reauthUrl = getReauthorizationUrl();
        const winowsAuth = window.open(reauthUrl, '_blank');

        timer = setInterval(async function () {
          if (winowsAuth === null) {
            clearInterval(timer);
          } else if (winowsAuth && winowsAuth.closed) {
            clearInterval(timer);
            try {
              await getPWCCurrentLoginStatus();
              dispatch(setPWCConnectedData({ isConnected: true }));
              resolve();
            } catch (error) {
              dispatch(initPWCConnectedData());
              reject(new Error('AsyncAuth.authFail'));
            }
          }
        }, CONSTANT.AUTH_CHECK_TIMER);
      } catch (e) {
        if (timer) clearInterval(timer);
      }
    });
  }, [dispatch]);

  const convertEmbeddedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.split('/');
      path[1] = 'embedded';
      urlObj.pathname = path.join('/');
      return urlObj.toString();
    } catch (err) {
      alert(err);
    }
  };

  const insertCanvas = useCallback(
    async (url) => {
      ga.event({ category: '액티브링크', action: '콘텐츠추가', label: '공동작업(캔버스)' });
      onDialogClose();

      try {
        const embeddedUrl = url ? convertEmbeddedUrl(url) : url;
        insert(embeddedUrl);
      } catch (err) {
        throw err;
      }
    },
    [insert, onDialogClose]
  );

  const isValidCanvas = async (docID) => {
    try {
      await getPWCDocInfo(docID);
    } catch (err) {
      throw new Error('Errors.InvalidCanvas');
    }
  };

  const isValidUrl = useCallback(async (url) => {
    try {
      const urlObj = new URL(url);
      const serverUrlObj = new URL(PWCServerURL);
      if (urlObj.host !== serverUrlObj.host) return false;

      const split = urlObj.pathname.split('/');
      const docID = split[split.length - 1];

      await isValidCanvas(docID);
      return true;
    } catch (err) {
      throw err;
    }
  }, []);

  const onInsertCanvas = useCallback(async () => {
    try {
      if (!pwcAuthData.isConnected) await reqLogin();

      if (insertInfo.type === INSERT_TYPE.EXIST_CANVAS) {
        await isValidUrl(insertInfo.url);
      }
      insertCanvas(insertInfo.type === INSERT_TYPE.EXIST_CANVAS ? insertInfo.url : null);
    } catch (err) {
      switch (err?.message) {
        case 'AsyncAuth.authFail':
        case 'Errors.InvalidCanvas':
          setWarning(t(`${err.message}`));
          break;
        default:
          setWarning(t(`Errors.InvalidUrl`));
          break;
      }
    }
  }, [insertCanvas, insertInfo, isValidUrl, pwcAuthData.isConnected, reqLogin, t]);

  return (
    <Dialog
      icon={IconCanvas}
      icon2x={IconCanvas2x}
      title={t('ActiveLink.Collaboration')}
      showTitleSeperateLine={true}
      showCloseBtn
      onClickCloseBtn={onDialogClose}
      isShow={isShow}>
      <DialogBody>
        {pwcAuthData.isConnected ? (
          <DialogBodyInsertCanvas>
            <RadioWrapper>
              {Object.keys(INSERT_TYPE).map((key) => (
                <Radio
                  value={key}
                  checked={key === insertInfo.type}
                  onChange={() => {
                    setInsertInfo({ type: key, url: '' });
                    setWarning('');
                  }}>
                  {t(`ActiveLink.${key}`)}
                </Radio>
              ))}
            </RadioWrapper>
            <InputArea disabled={true}>
              <div style={{ width: '23px' }}></div>
              <InputWrapper>
                <TextInput
                  disabled={insertInfo.type !== INSERT_TYPE.EXIST_CANVAS}
                  value={insertInfo.url}
                  onChange={(e) => setInsertInfo((prev) => ({ ...prev, url: e.target.value }))}
                  onSubmit={onInsertCanvas}></TextInput>
                <Warning>{warning}</Warning>
              </InputWrapper>
            </InputArea>
          </DialogBodyInsertCanvas>
        ) : (
          <DialogMessage>
            {pwcAuthData.isConnected ? (
              <Trans i18nKey="ActiveLink.InsertCanvasMessage"></Trans>
            ) : (
              <Trans i18nKey="ActiveLink.InsertCanvasDescriptionWithAuth"></Trans>
            )}
          </DialogMessage>
        )}
      </DialogBody>
      <FooterWrapper>
        <ButtonWapper>
          <Button
            autoFocus={true}
            cssExt={css`
              font-size: 13px;
              font-weight: 700;
              background: #1d7ff9;
              border-radius: 2px;
              color: #fff;
              border: none;
              width: 81px;
              height: 30px;
              margin-right: 12px;
              &:hover {
                background: #0b8793;
                opacity: 0.4;
                cursor: pointer;
              }
            `}
            onClick={
              pwcAuthData.isConnected
                ? () => onInsertCanvas()
                : async () => {
                    try {
                      await reqLogin();
                    } catch (err) {
                      setWarning(t(`${err.message}`));
                    }
                  }
            }>
            {pwcAuthData.isConnected ? t('Common.Insert') : t('ActiveLink.Connect')}
          </Button>
          <Button
            cssExt={css`
              font-size: 13px;
              border-radius: 2px;
              color: #2f3133;
              border: 1px solid #b1b6bb;
              width: 81px;
              height: 30px;
            `}
            onClick={() => dispatch(closeDialog())}>
            {t('Common.Cancel')}
          </Button>
        </ButtonWapper>
      </FooterWrapper>
    </Dialog>
  );
}
